import { useState } from "react";
import { useHistory } from "react-router-dom";
import Button from "react-bootstrap/Button";
import Col from "react-bootstrap/Col";
import Form from "react-bootstrap/Form";
import Row from "react-bootstrap/Row";
import Spinner from "react-bootstrap/Spinner";
import Alert from "react-bootstrap/Alert";
import { useSnackbar } from "react-simple-snackbar";
import { API } from "./../../../http-common";
import { validateInputDni, sanitizeDniInput } from "../../../hooks/ValidateInput";
import "../../../assets/css/subusers_page.css";

const optionsError = {
  position: "bottom-right",
  style: {
    backgroundColor: "var(--bs-red)",
    border: "0",
    color: "#fff",
    fontSize: "20px",
    textAlign: "center",
    marginBottom: "5rem",
  },
  closeStyle: { color: "#fff", fontSize: "16px", marginRight: "1rem" },
};

const optionsSuccess = {
  position: "bottom-right",
  style: {
    backgroundColor: "var(--bs-green)",
    border: "0",
    color: "#fff",
    fontSize: "20px",
    textAlign: "center",
    marginBottom: "5rem",
  },
  closeStyle: { color: "#fff", fontSize: "16px", marginRight: "1rem" },
};

export const FormPrincipalUser = () => {
  const history = useHistory();

  const [openSnackbarSuccess] = useSnackbar(optionsSuccess);
  const [openSnackbarError] = useSnackbar(optionsError);

  const [principalDocument, setPrincipalDocument] = useState("");
  const [searchStatus, setSearchStatus] = useState("idle"); // idle | searching | found | not_found | created
  const [principalFound, setPrincipalFound] = useState(null);

  const [principalNew, setPrincipalNew] = useState({
    first_name: "",
    last_name: "",
    email: "",
  });
  const [creatingPrincipal, setCreatingPrincipal] = useState(false);

  const searchPrincipal = async () => {
    if (!validateInputDni(principalDocument)) return;

    setSearchStatus("searching");
    setPrincipalFound(null);

    try {
      const response = await API.get(`/prospects/find`, {
        params: { document: principalDocument.trim() },
      });
      setPrincipalFound(response.data);
      setSearchStatus("found");
    } catch (error) {
      if (error.response?.status === 404) {
        setSearchStatus("not_found");
      } else {
        setSearchStatus("idle");
        openSnackbarError("No se pudo buscar el DNI, intentá de nuevo");
      }
    }
  };

  const handlePrincipalNewChange = (field) => (e) =>
    setPrincipalNew((prev) => ({ ...prev, [field]: e.target.value }));

  const goToPrincipalDetails = () => {
    history.push({
            pathname: '/admin/details',
            search: '',
            state: { candidato_id: principalFound.id, is_balanz: principalFound.is_balanz ?? 0 },
        });
  };

  const goToListing = () => history.push("/admin");

  const resetAll = () => {
    setPrincipalDocument("");
    setSearchStatus("idle");
    setPrincipalFound(null);
    setPrincipalNew({ first_name: "", last_name: "", email: "" });
  };

  // Crea el productor con contrato manual (solo cuando no se encontró por DNI)
  const handleCreatePrincipal = async (e) => {
    e.preventDefault();

    setCreatingPrincipal(true);
    try {
      const response = await API.post(`/prospects/manual-principal`, {
        first_name: principalNew.first_name,
        last_name: principalNew.last_name,
        document: principalDocument.trim(),
        email: principalNew.email,
      });
      openSnackbarSuccess("Productor creado correctamente");
      setPrincipalFound(response.data.data);
      setSearchStatus("created");
    } catch (error) {
      const errors = error.response?.data?.errors;
      openSnackbarError(
        errors?.[0]?.msg || "No se pudo crear el productor, intentá de nuevo",
      );
    } finally {
      setCreatingPrincipal(false);
    }
  };

  return (
    <div className="subuser-form-wrapper">
      <h3 className="subuser-form-title">Crear nuevo productor</h3>

      {/* Búsqueda del principal por DNI */}
      <Row className="mb-3">
        <Form.Group as={Col} md={8} controlId="formDniPrincipal">
          <Form.Label>DNI del usuario principal</Form.Label>
          <Form.Control
            type="text"
            placeholder="Ingrese DNI"
            inputMode="numeric"
            maxLength={8}
            value={principalDocument}
            onChange={(e) => {
              setPrincipalDocument(sanitizeDniInput(e.target.value));
              setSearchStatus("idle");
              setPrincipalFound(null);
            }}
            onKeyDown={(e) =>
              e.key === "Enter" && (e.preventDefault(), searchPrincipal())
            }
          />
        </Form.Group>

        <Form.Group as={Col} md={4} className="d-flex align-items-end">
          <Button
            variant="outline-primary"
            onClick={searchPrincipal}
            disabled={searchStatus === "searching" || !validateInputDni(principalDocument)}
            className="w-100 mt-1"
          >
            {searchStatus === "searching" ? (
              <Spinner animation="border" size="sm" />
            ) : (
              "Buscar"
            )}
          </Button>
        </Form.Group>
      </Row>

      {/* Caso: principal ya existe */}
      {searchStatus === "found" && principalFound && (
        <>
          <Alert variant="warning" className="mb-3">
            {principalFound.parent_prospect_id ? (
              <>
                Ya existe un <strong>subusuario</strong> con ese DNI:{" "}
                <strong>{principalFound.complete_name}</strong> ({principalFound.email})
                {principalFound.parent_complete_name && (
                  <>
                    , asociado al productor{" "}
                    <strong>{principalFound.parent_complete_name}</strong>
                  </>
                )}
              </>
            ) : (
              <>
                Productor con ese DNI ya existente:{" "}
                <strong>{principalFound.complete_name}</strong> ({principalFound.email})
              </>
            )}
          </Alert>
          <div className="subuser-form-actions mb-3">
            <Button variant="primary" onClick={goToPrincipalDetails}>
              {principalFound.parent_prospect_id
                ? "Ir a la ficha del subusuario"
                : "Ir a la ficha"}
            </Button>
          </div>
        </>
      )}

      {/* Caso: principal recién creado */}
      {searchStatus === "created" && principalFound && (
        <>
          <Alert variant="success" className="mb-3">
            Productor creado correctamente
          </Alert>
          <div className="subuser-form-actions subuser-form-actions--spread mb-3">
            <Button variant="primary" onClick={goToPrincipalDetails}>
              Ir a la ficha del productor
            </Button>
            <Button variant="outline-secondary" onClick={goToListing}>
              Volver al listado
            </Button>
          </div>
        </>
      )}

      {/* Caso: DNI no encontrado -> form para crear el productor (contrato manual) */}
      {searchStatus === "not_found" && (
        <>
          <Alert variant="warning" className="mb-3">
            No se encontró un productor con ese DNI. Completá sus datos para
            darlo de alta con el estado marcado como que se realizó contrato manual.
          </Alert>

          <Form onSubmit={handleCreatePrincipal}>
            <h6 className="subuser-section-title">Crear Productor</h6>
            <Row className="mb-3">
              <Form.Group as={Col} controlId="formNombrePrincipal">
                <Form.Label>Nombre</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Ingrese nombre"
                  value={principalNew.first_name}
                  onChange={handlePrincipalNewChange("first_name")}
                  required
                />
              </Form.Group>
              <Form.Group as={Col} controlId="formApellidoPrincipal">
                <Form.Label>Apellido</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Ingrese apellido"
                  value={principalNew.last_name}
                  onChange={handlePrincipalNewChange("last_name")}
                  required
                />
              </Form.Group>
            </Row>
            <Row className="mb-3">
              <Form.Group as={Col} controlId="formEmailPrincipal">
                <Form.Label>E-mail</Form.Label>
                <Form.Control
                  type="email"
                  placeholder="Ingrese e-mail"
                  value={principalNew.email}
                  onChange={handlePrincipalNewChange("email")}
                  required
                />
              </Form.Group>
            </Row>

            <div className="subuser-form-actions">
              <Button
                variant="primary"
                type="submit"
                disabled={creatingPrincipal}
              >
                {creatingPrincipal ? (
                  <Spinner animation="border" size="sm" />
                ) : (
                  "Crear productor"
                )}
              </Button>
            </div>
          </Form>
        </>
      )}
    </div>
  );
};
